/**
 * Расчёт прогноза позиции.
 *
 * Инструмент рисует три цены — вход, стоп и цель, — но ценность его не
 * в рамках, а в числах: сколько брать объёма, чем рискуешь и что
 * получишь. Считается это здесь, отдельно от рисования, потому что
 * ошибка в объёме позиции стоит денег, а ошибка в цвете рамки — нет.
 *
 * Все величины выводятся из трёх цен и настроек счёта. Ничего не
 * додумывается: если стоп совпал со входом, объём равен нулю, а не
 * бесконечности.
 */

import type { DrawingKind } from './kinds.js';

export type RiskUnit = 'percent' | 'money';

/** Что писать в средней подписи. */
export type PositionInfo = 'full' | 'short' | 'none';

export interface PositionOptions {
  /** Размер счёта в валюте котировки. */
  readonly accountSize: number;
  /** Размер лота: во сколько единиц базовой валюты укладывается один лот. */
  readonly lotSize: number;
  readonly risk: number;
  readonly riskUnit: RiskUnit;
  readonly leverage: number;
  /** Знаков в объёме. null — по точности инструмента. */
  readonly qtyPrecision: number | null;

  readonly lineColor: string;
  readonly stopColor: string;
  readonly targetColor: string;
  readonly fontSize: number;
  /** Подписи цен у линий входа, стопа и цели. */
  readonly priceLabels: boolean;
  /** Сокращать крупные суммы: 12.5K вместо 12 500. */
  readonly compact: boolean;
  /** Показывать подписи всегда, а не только под курсором. */
  readonly alwaysShow: boolean;
  readonly info: PositionInfo;
  /**
   * Сколько целей: одна или частичные фиксации по пути к главной.
   *
   * Промежуточные цели делят путь до главной поровну — так их ставят
   * чаще всего: треть позиции на первой, треть на второй, остаток на
   * последней. Тянуть их по отдельности незачем — они следуют за целью.
   */
  readonly targets: 1 | 2 | 3;
}

export const DEFAULT_POSITION_OPTIONS: PositionOptions = {
  accountSize: 1000,
  lotSize: 1,
  risk: 1,
  riskUnit: 'percent',
  leverage: 1,
  qtyPrecision: null,

  lineColor: '#79818F',
  stopColor: '#F0616F',
  targetColor: '#3FBF87',
  fontSize: 11,
  priceLabels: true,
  compact: true,
  alwaysShow: false,
  info: 'full',
  targets: 1,
};

export interface PositionResult {
  /** LONG или SHORT — от него зависит, где прибыль, а где убыток. */
  readonly long: boolean;
  readonly entry: number;
  readonly stop: number;
  readonly target: number;

  /** Риск на единицу базовой валюты. */
  readonly riskPerUnit: number;
  readonly rewardPerUnit: number;
  /** Сколько денег в риске по настройкам счёта. */
  readonly riskMoney: number;
  /** Объём позиции в базовой валюте. */
  readonly quantity: number;
  /** Он же в лотах. */
  readonly lots: number;
  /** Упёрлись ли в плечо: объём по риску оказался больше доступного. */
  readonly cappedByLeverage: boolean;

  readonly loss: number;
  readonly profit: number;
  /** Отношение прибыли к риску. null — риска нет, делить не на что. */
  readonly ratio: number | null;

  readonly stopPercent: number;
  readonly targetPercent: number;
  readonly stopTicks: number;
  readonly targetTicks: number;
}

/**
 * Полный расчёт по трём ценам.
 *
 * `tickSize` — шаг цены инструмента: в тиках расстояние до стопа
 * читается привычнее, чем в долларах, и именно так его задают биржи.
 */
export function computePosition(
  kind: DrawingKind,
  entry: number,
  stop: number,
  target: number,
  options: PositionOptions,
  tickSize: number,
): PositionResult {
  const long = kind !== 'short-position';

  const riskPerUnit = Math.abs(entry - stop);
  const rewardPerUnit = Math.abs(target - entry);

  const riskMoney =
    options.riskUnit === 'percent'
      ? (options.accountSize * options.risk) / 100
      : options.risk;

  // Объём по риску: столько единиц, чтобы потеря на стопе равнялась
  // заданному риску. Нулевой стоп означает нулевой объём, а не деление
  // на ноль.
  const byRisk = riskPerUnit > 0 ? riskMoney / riskPerUnit : 0;

  // Плечо ограничивает объём сверху: больше, чем позволяет счёт,
  // взять нельзя, каким бы узким ни был стоп.
  const byLeverage = entry > 0 ? (options.accountSize * options.leverage) / entry : 0;
  const cappedByLeverage = byRisk > byLeverage && byLeverage > 0;
  const quantity = Math.max(0, Math.min(byRisk, byLeverage || byRisk));

  return {
    long,
    entry,
    stop,
    target,
    riskPerUnit,
    rewardPerUnit,
    riskMoney,
    quantity,
    lots: options.lotSize > 0 ? quantity / options.lotSize : 0,
    cappedByLeverage,
    loss: riskPerUnit * quantity,
    profit: rewardPerUnit * quantity,
    ratio: riskPerUnit > 0 ? rewardPerUnit / riskPerUnit : null,
    stopPercent: entry > 0 ? ((stop - entry) / entry) * 100 : 0,
    targetPercent: entry > 0 ? ((target - entry) / entry) * 100 : 0,
    stopTicks: tickSize > 0 ? Math.round(riskPerUnit / tickSize) : 0,
    targetTicks: tickSize > 0 ? Math.round(rewardPerUnit / tickSize) : 0,
  };
}

/** Настройки фигуры: сохранённые поверх умолчаний. */
export function positionOptionsOf(stored: Partial<PositionOptions> | undefined): PositionOptions {
  return stored ? { ...DEFAULT_POSITION_OPTIONS, ...stored } : DEFAULT_POSITION_OPTIONS;
}

export function isPosition(kind: DrawingKind): boolean {
  return kind === 'long-position' || kind === 'short-position';
}

/** Сумма для подписи: сокращённо или полностью. */
export function money(value: number, compact: boolean): string {
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);

  if (compact) {
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(2)}K`;
  }
  return `${sign}${abs.toFixed(2)}`;
}

/** Объём: по заданной точности или по точности инструмента. */
export function quantityText(
  value: number,
  options: PositionOptions,
  fallbackPrecision: number,
): string {
  const digits = options.qtyPrecision ?? fallbackPrecision;
  return value.toFixed(Math.max(0, Math.min(8, digits)));
}

/** Приведение сохранённых настроек к текущей форме. */
export function normalizePositionOptions(value: unknown): PositionOptions | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Partial<PositionOptions>;

  const positive = (input: unknown, fallback: number): number =>
    typeof input === 'number' && Number.isFinite(input) && input > 0 ? input : fallback;

  return {
    ...DEFAULT_POSITION_OPTIONS,
    ...raw,
    // Ноль и мусор в размере счёта, лоте и плече обнулили бы весь
    // расчёт молча — здесь возвращаемся к умолчанию.
    accountSize: positive(raw.accountSize, DEFAULT_POSITION_OPTIONS.accountSize),
    lotSize: positive(raw.lotSize, DEFAULT_POSITION_OPTIONS.lotSize),
    leverage: positive(raw.leverage, DEFAULT_POSITION_OPTIONS.leverage),
    risk:
      typeof raw.risk === 'number' && Number.isFinite(raw.risk) && raw.risk >= 0
        ? raw.risk
        : DEFAULT_POSITION_OPTIONS.risk,
    qtyPrecision:
      typeof raw.qtyPrecision === 'number' && Number.isFinite(raw.qtyPrecision)
        ? raw.qtyPrecision
        : null,
  };
}

/**
 * Показывать ли числа у бокса.
 *
 * Три плашки на каждом прогнозе превратили бы график в таблицу, поэтому
 * по умолчанию они всплывают. Но пока бокс натягивают или тянут за
 * угол, числа нужны обязательно: ради них его и рисуют, и смотреть на
 * них надо в движении, а не после того, как отпустили мышь.
 */
export function showsNumbers(
  options: PositionOptions,
  id: string,
  hoveredId: string | null,
  activeId: string | null,
): boolean {
  return options.alwaysShow || hoveredId === id || activeId === id;
}
